// 렌더러·장면·안개·크기 맞춤·프레임 루프.
// 색은 프로토타입(three r128)과 같게: 색 관리 끔 + Linear 출력. (docs/decisions.md 참고, 임시)
import * as THREE from 'three';

THREE.ColorManagement.enabled = false;

// 점광원 거리 감쇠도 r128 방식으로: (1 - 거리/cutoff)^decay. 0.169의 물리 기반 1/거리^2는 등불 가까이가 지나치게 밝아진다.
// 세기는 다른 조명처럼 π를 곱해 쓴다 (lights.ts LEGACY). 색 관리 전환 때 함께 되돌린다 (docs/decisions.md).
{
  const before = THREE.ShaderChunk.lights_pars_begin;
  THREE.ShaderChunk.lights_pars_begin = before.replace(/float getDistanceAttenuation\([\s\S]*?return distanceFalloff;\s*\}/, [
    'float getDistanceAttenuation( const in float lightDistance, const in float cutoffDistance, const in float decayExponent ) {',
    '\tif ( cutoffDistance > 0.0 && decayExponent > 0.0 ) return pow( saturate( - lightDistance / cutoffDistance + 1.0 ), decayExponent );',
    '\treturn 1.0;',
    '}',
  ].join('\n'));
  if (THREE.ShaderChunk.lights_pars_begin === before) throw new Error('getDistanceAttenuation 패치 실패: three 버전을 확인하세요');
}

export const ENGINE = {
  maxPixelRatio: 2,
  shadowMapSize: 2048,
  /** 한 프레임 dt 상한 (탭 전환 뒤 순간이동 방지). */
  maxDt: 0.05,
};

export interface Stage {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  fog: THREE.Fog | null;
  camera: THREE.PerspectiveCamera;
  clock: THREE.Clock;
  /** 크기가 바뀔 때 (w, h, aspect). */
  onResize: Set<(w: number, h: number, aspect: number) => void>;
  /** 매 프레임 (t, dt). 등록 순서대로 부른 뒤 렌더한다. */
  onTick: Set<(t: number, dt: number) => void>;
  start(): void;
}

export function createStage(canvas: HTMLCanvasElement, opts: { clearColor: THREE.ColorRepresentation; fog?: [number, number] }): Stage {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, ENGINE.maxPixelRatio));
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.setClearColor(opts.clearColor, 1);

  const scene = new THREE.Scene();
  const fog = opts.fog ? new THREE.Fog(opts.clearColor, opts.fog[0], opts.fog[1]) : null;
  scene.fog = fog;
  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 700);
  const clock = new THREE.Clock();
  const onResize = new Set<(w: number, h: number, aspect: number) => void>();
  const onTick = new Set<(t: number, dt: number) => void>();

  const host = canvas.parentElement ?? document.body;
  function resize() {
    const w = host.clientWidth, h = host.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    onResize.forEach((f) => f(w, h, camera.aspect));
    camera.updateProjectionMatrix();
  }
  new ResizeObserver(resize).observe(host);

  let lastT = 0;
  function tick() {
    const t = clock.getElapsedTime(), dt = Math.min(t - lastT, ENGINE.maxDt);
    lastT = t;
    onTick.forEach((f) => f(t, dt));
    renderer.render(scene, camera);
  }

  return {
    renderer, scene, fog, camera, clock, onResize, onTick,
    start() { resize(); renderer.setAnimationLoop(tick); },
  };
}
